import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ChevronDown, ChevronRight, ChevronLeft, Search
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { usePermissions } from '../hooks/usePermissions'
import { getRoleBasedNavigation } from '../config/roleBasedNavigation'
import { getSidebarTheme } from '../config/sidebarThemes'
import {
  FINANCIAL_GROUP_LABELS,
  FINANCIAL_GROUP_ORDER,
  FINANCIAL_ROUTE_CONFIG,
  FINANCIAL_ROUTE_MAP,
} from '../config/financialRoutes'
import schoolCrest from '../assets/logos/crest.png'

const SCHOOL_NAME = 'The Promise Standard School'

// Where the gradient settles when the pointer isn't over the sidebar.
const FLOW_REST = 0.3

/**
 * Drives the `--sb-flow` CSS variable from the pointer's vertical position,
 * easing toward it so the colour flows rather than snaps. Writes straight to
 * the element's style (no React re-renders) and only runs frames while
 * catching up. Skipped entirely for users who prefer reduced motion.
 */
function useFlowingGradient(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let target = FLOW_REST
    let current = FLOW_REST
    let frame = 0

    const tick = () => {
      current += (target - current) * 0.1
      if (Math.abs(target - current) < 0.001) current = target
      el.style.setProperty('--sb-flow', current.toFixed(4))
      frame = current === target ? 0 : requestAnimationFrame(tick)
    }
    const flowTo = (value: number) => {
      target = Math.min(1, Math.max(0, value))
      if (!frame) frame = requestAnimationFrame(tick)
    }
    const onMove = (event: PointerEvent) => {
      const rect = el.getBoundingClientRect()
      flowTo((event.clientY - rect.top) / rect.height)
    }
    const onLeave = () => flowTo(FLOW_REST)

    el.addEventListener('pointermove', onMove)
    el.addEventListener('pointerleave', onLeave)
    return () => {
      el.removeEventListener('pointermove', onMove)
      el.removeEventListener('pointerleave', onLeave)
      cancelAnimationFrame(frame)
    }
  }, [ref])
}

interface SidebarProps {
  isCollapsed?: boolean
  onToggle?: () => void
}

export default function EnhancedSidebar({ isCollapsed = false, onToggle }: SidebarProps) {
  const location = useLocation()
  const { user } = useAuthStore()
  const { canAccess, hasRole, hasCapability } = usePermissions()
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set(['primary', 'secondary', 'tertiary']))
  const [searchQuery, setSearchQuery] = useState('')
  const asideRef = useRef<HTMLElement>(null)
  useFlowingGradient(asideRef)

  // Keep the menu that holds the current page open, however the user got
  // there (sidebar, in-page link, back button). Matched through the children
  // too, since a child can live outside its parent's path (/scholarships
  // sits under Financial for the bursar).
  const userRole = user?.user_type
  useEffect(() => {
    if (!userRole) return
    const path = location.pathname
    const matches = (href: string) => path === href || path.startsWith(`${href}/`)
    const toOpen = getRoleBasedNavigation(userRole)
      .filter((item) => item.children?.length && (matches(item.href) || item.children.some((child) => matches(child.href))))
      .map((item) => item.href)
    if (toOpen.length === 0) return
    setExpandedSections((previous) => (
      toOpen.every((href) => previous.has(href)) ? previous : new Set([...previous, ...toOpen])
    ))
  }, [location.pathname, userRole])

  if (!user) return null

  const theme = getSidebarTheme(user.user_type)
  const themeStyle = {
    '--sb-deep': theme.deep,
    '--sb-light': theme.light,
    '--sb-accent': theme.accent,
  } as React.CSSProperties

  // Get role-specific navigation
  const allNavigationItems = getRoleBasedNavigation(user.user_type)

  // Helper to derive resource key from href for permission checks
  const getResourceKey = (href: string): string => {
    try {
      const path = href.startsWith('/') ? href.slice(1) : href
      const first = path.split('/')[0] || ''
      return first
    } catch {
      return ''
    }
  }

  const canShowItem = (item: any) => {
    // Ensure role eligibility first
    const roleOk = !item.roles || item.roles.includes('*') || item.roles.includes(user.user_type) || hasRole(item.roles)
    if (!roleOk) return false

    // Always allow dashboards for that role
    if (item.href.includes('/dashboard')) return true

    // Governance users are deliberately outside the employee-role access
    // map; their server-resolved, read-only capability is the authority for
    // showing this workspace.
    if (item.href === '/governance') {
      return hasCapability('financial.view_governance_dashboard')
    }

    // Apply resource permission where applicable
    const resource = getResourceKey(item.href)
    return resource ? canAccess(resource) : true
  }

  // Keep parent navigation visible when at least one permitted child exists.
  const filterNavigationTree = (items: any[]): any[] => items.reduce((visible, item) => {
    if (!canShowItem(item)) return visible
    const children = item.children ? filterNavigationTree(item.children) : []
    if (!item.children || children.length > 0) {
      visible.push({ ...item, children })
    }
    return visible
  }, [])

  const baseFiltered = filterNavigationTree(allNavigationItems)

  const searchNavigationTree = (items: any[]): any[] => items.reduce((matches, item) => {
    const children = item.children ? searchNavigationTree(item.children) : []
    if (item.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      matches.push(item)
    } else {
      matches.push(...children)
    }
    return matches
  }, [])

  // Search includes nested links while normal navigation preserves hierarchy.
  const filteredNavigation = searchQuery ? searchNavigationTree(baseFiltered) : baseFiltered

  // Group navigation items by category
  const groupedNavigation = {
    primary: filteredNavigation.filter(item => item.priority && item.priority <= 5),
    secondary: filteredNavigation.filter(item => item.priority && item.priority > 5 && item.priority <= 10),
    tertiary: filteredNavigation.filter(item => item.priority && item.priority > 10)
  }

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections)
    if (newExpanded.has(section)) {
      newExpanded.delete(section)
    } else {
      newExpanded.add(section)
    }
    setExpandedSections(newExpanded)
  }

  const expandSection = (section: string) => {
    setExpandedSections((previous) => (previous.has(section) ? previous : new Set(previous).add(section)))
  }

  // Finance pages carry a group in financialRoutes.ts; show them under those
  // headings, in that file's order, so the sidebar matches the in-page tabs.
  // Children without a group keep their own order after the grouped ones.
  // A child can also name its group directly when its page isn't a finance
  // route; those follow the finance pages within the group.
  const groupChildren = (children: any[]): { label?: string; items: any[] }[] => {
    const groupOf = (child: any) => child.group ?? FINANCIAL_ROUTE_MAP.get(child.href)?.group
    const routeIndex = (child: any) => {
      const index = FINANCIAL_ROUTE_CONFIG.findIndex((route) => route.path === child.href)
      return index === -1 ? FINANCIAL_ROUTE_CONFIG.length : index
    }
    const grouped = FINANCIAL_GROUP_ORDER.map((group) => ({
      label: FINANCIAL_GROUP_LABELS[group],
      items: children
        .filter((child) => groupOf(child) === group)
        .sort((a, b) => routeIndex(a) - routeIndex(b)),
    })).filter((group) => group.items.length > 0)
    if (grouped.length === 0) return [{ items: children }]
    const rest = children.filter((child) => !grouped.some((group) => group.items.includes(child)))
    return rest.length ? [...grouped, { items: rest }] : grouped
  }

  const renderNavigationItem = (item: any, depth = 0): React.ReactNode => {
    const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
    const isCurrentPage = location.pathname === item.href
    const hasChildren = item.children?.length > 0
    const isExpanded = expandedSections.has(item.href)
    const isChild = depth > 0

    return (
      <div key={item.href}>
        <div className="flex items-center gap-1">
          <Link
            to={item.href}
            // A parent link both opens its page and reveals what's inside it.
            onClick={hasChildren ? () => expandSection(item.href) : undefined}
            aria-current={isCurrentPage ? 'page' : undefined}
            className={`group flex min-w-0 flex-1 items-center rounded-md font-medium transition-all duration-200 ${
              isChild ? 'px-2.5 py-1.5 text-[13px]' : 'px-3 py-2 text-sm'
            } ${
              isActive && (!hasChildren || isCurrentPage)
                ? 'bg-white/15 text-white shadow-sm ring-1 ring-inset ring-white/10'
                : isActive
                  ? 'text-white'
                  : 'text-white/75 hover:bg-white/10 hover:text-white'
            }`}
          >
            <item.icon className={`flex-shrink-0 ${isChild ? 'mr-2.5 h-4 w-4' : 'mr-3 h-5 w-5'} ${isActive ? 'text-[color:var(--sb-accent)]' : 'text-white/55 group-hover:text-white'}`} />
            <span className="truncate">{item.name}</span>
            {isActive && !hasChildren && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-[color:var(--sb-accent)]" />}
          </Link>
          {hasChildren && (
            <button
              type="button"
              onClick={() => toggleSection(item.href)}
              className="rounded-md p-1 text-white/60 hover:bg-white/10 hover:text-white"
              aria-label={isExpanded ? `Collapse ${item.name}` : `Expand ${item.name}`}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="mb-2 ml-5 mt-1 border-l border-white/15 pl-2">
            {groupChildren(item.children).map((group, index) => (
              <div key={group.label ?? `group-${index}`} className={index > 0 ? 'mt-3' : ''}>
                {group.label && (
                  <p className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[color:var(--sb-accent)] opacity-80">
                    {group.label}
                  </p>
                )}
                <div className="space-y-0.5">{group.items.map((child) => renderNavigationItem(child, depth + 1))}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderNavigationSection = (items: any[], sectionName: string, sectionTitle: string) => {
    if (items.length === 0) return null

    const isExpanded = expandedSections.has(sectionName)

    return (
      <div className="mb-4">
        <button
          onClick={() => toggleSection(sectionName)}
          type="button"
          aria-expanded={isExpanded}
          className="flex w-full items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white/50 transition-colors hover:text-white"
        >
          <span>{sectionTitle}</span>
          {isExpanded ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>
        
        {isExpanded && (
          <div className="space-y-1">
            {items.map((item) => renderNavigationItem(item))}
          </div>
        )}
      </div>
    )
  }

  return (
    <aside
      ref={asideRef}
      style={themeStyle}
      data-sidebar-theme={theme.family}
      className={`${isCollapsed ? 'w-16' : 'w-64'} sidebar-flow sticky top-0 z-20 shrink-0 border-r border-black/20 text-white shadow-[0_0_40px_rgba(15,23,42,0.35)] transition-[width] duration-300 flex h-screen flex-col`}
    >
      {/* Header */}
      <div className="border-b border-white/10 p-4">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white p-0.5 shadow-lg shadow-black/30">
                <img src={schoolCrest} alt="" aria-hidden="true" className="h-full w-full object-contain" />
              </div>
              <div className="min-w-0">
                <h1 className="text-[13px] font-semibold leading-tight text-white">{SCHOOL_NAME}</h1>
                <p className="mt-1 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-[color:var(--sb-accent)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--sb-accent)]" aria-hidden="true" />
                  {theme.label}
                </p>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div title={SCHOOL_NAME} className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg bg-white p-0.5 shadow-lg shadow-black/30">
              <img src={schoolCrest} alt="" aria-hidden="true" className="h-full w-full object-contain" />
            </div>
          )}
          {onToggle && (
            <button
              type="button"
              onClick={onToggle}
              className="ml-auto rounded-md p-1 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              title={isCollapsed ? 'Expand sidebar' : 'Minimize sidebar'}
              aria-label={isCollapsed ? 'Expand sidebar' : 'Minimize sidebar'}
            >
              {isCollapsed ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            </button>
          )}
        </div>
      </div>

      {/* Search */}
      {!isCollapsed && (
        <div className="border-b border-white/10 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/50" />
            <input
              type="text"
              placeholder="Search navigation..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full rounded-md border border-white/15 bg-black/20 py-2 pl-10 pr-4 text-sm text-white placeholder:text-white/50 focus:border-[color:var(--sb-accent)] focus:outline-none focus:ring-2 focus:ring-[color:var(--sb-accent)]/40"
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className={`flex-1 overflow-y-auto scrollbar-hover py-4 ${isCollapsed ? 'px-2' : 'px-4'}`}>
        {isCollapsed ? (
          // Icon-only rail: no labels, section headers, or nested children --
          // keeps the collapsed w-16 width from overflowing.
          <div className="space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  title={item.name}
                  aria-label={item.name}
                  className={`flex items-center justify-center rounded-md p-2 transition-colors ${
                    isActive
                      ? 'bg-white/15 text-[color:var(--sb-accent)]'
                      : 'text-white/60 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                </Link>
              )
            })}
          </div>
        ) : searchQuery ? (
          // Show filtered results when searching
          <div className="space-y-1">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname.startsWith(item.href)
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200 ${
                    isActive
                      ? 'border-l-2 border-[color:var(--sb-accent)] bg-white/15 text-white shadow-sm'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      isActive ? 'text-[color:var(--sb-accent)]' : 'text-white/60 group-hover:text-white'
                    }`}
                  />
                  <span className="truncate">{item.name}</span>
                  {isActive && (
                    <div className="ml-auto">
                      <div className="h-2 w-2 rounded-full bg-[color:var(--sb-accent)]"></div>
                    </div>
                  )}
                </Link>
              )
            })}
          </div>
        ) : (
          // Show categorized navigation
          <div className="space-y-4">
            {renderNavigationSection(groupedNavigation.primary, 'primary', 'Workspace')}
            {renderNavigationSection(groupedNavigation.secondary, 'secondary', 'General')}
            {renderNavigationSection(groupedNavigation.tertiary, 'tertiary', 'More')}
          </div>
        )}

      </nav>
    </aside>
  )
}
